<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox title="Payment Received" avatarImage="/glass/img/accounts-overview-paymentreceived.png" :avatarCircleMode="false" :loading="pageDataLoading">
          <h4>Payment #{{paymentReceived.sequenceNumber}}</h4>

          <template v-if="hasIntegrations">
            <h5>Integrations</h5>
            <p>
              <LinkIntegrationLink v-for="integration in integrationList" :key="integration.code" :integration="integration" />
            </p>
          </template>

          <OverviewBoxInfoItem v-if="paymentReceived.contact" icon="fa-user-circle">
            <LinkContact :id="paymentReceived.contactId" :name="paymentReceived.contact.name" />
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem v-if="paymentReceived.order" icon="fa-shopping-cart">
            <LinkOrder :id="paymentReceived.orderId" :name="paymentReceived.order.sequenceNumber" />
          </OverviewBoxInfoItem>

          <OverviewBoxInfoItem icon="fa-tag" v-if="paymentReceived.reference">
            {{paymentReceived.reference}}
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem icon="fa-tag" v-if="paymentReceived.description">
            {{paymentReceived.description}}
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem icon="fa-usd-circle">
            {{paymentReceived.amount | formatCurrency}}
          </OverviewBoxInfoItem>
          
          <OverviewBoxList>
            <OverviewBoxListItem title="Status">
              <LabelBadge :labelClass="`label-payment-status-${paymentReceived.status}`">{{paymentReceived.status | formatLookupName(PaymentStatusTypeLookup)}}</LabelBadge>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Type">
              <LabelBadge :labelClass="`label-payment-type-${paymentReceived.paymentType}`">{{paymentReceived.paymentType | formatLookupName(PaymentTypeLookup)}}</LabelBadge>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Number" v-if="paymentReceived.sequenceNumber">{{paymentReceived.sequenceNumber}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Payment Date">{{paymentReceived.datePayment | formatDate}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Received By" v-if="paymentReceived.user">
              <LinkUser :id="paymentReceived.user.id" :name="paymentReceived.user.name" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Created On">{{paymentReceived.createdAt | formatDate}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Last Updated">{{paymentReceived.updatedAt | formatDate}}</OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>

      </div>

      <div class="col-sm-10">
        <TabbedBox ref="tabBox" :loading="pageDataLoading">
          <template v-slot:tools>
            
          </template>

          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-payment-summary">
              <i class="far fa-tasks"></i> Summary
            </Tab>
            <Tab tabid="tab-payment-activitylog">
              <i class="far fa-history"></i> Activity Log
            </Tab>
          </template>

          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-payment-summary">
              <div v-if="paymentReceived.id">
                <DataPageArea title="Details">
                  <template slot="actionArea">
                    <ActionDropMenu class="float-right">
                      <DropdownMenuItem @click="updatePaymentReceived" icon="fa-pencil" v-if="canEditPayment">Edit Payment Details</DropdownMenuItem>
                      <DropdownMenuItem @click="cancelPaymentReceived" icon="fa-times" v-if="canCancelPayment">Cancel Payment</DropdownMenuItem>
                      <DropdownMenuDivider v-if="(canEditPayment || canCancelPayment) && !isPaymentCancelled" />
                      <DropdownMenuItem @click="sendPaymentReceipt" icon="fa-envelope" v-if="!isPaymentCancelled">Send Payment Receipt</DropdownMenuItem>
                      <DropdownMenuItem @click="displayPaymentReceivedReport()" icon="fa-print" v-if="!isPaymentCancelled">Payment Receipt</DropdownMenuItem>
                    </ActionDropMenu>
                  </template>
                  <template slot="dataArea">
                    <DataItem title="Payment Number">{{paymentReceived.sequenceNumber}}</DataItem>
                    <DataItem title="Description">{{paymentReceived.description}}</DataItem>
                    <DataItem title="Reference">{{paymentReceived.reference}}</DataItem>
                    <DataItem title="Amount">{{paymentReceived.amount | formatCurrency}}</DataItem>
                    <DataItem title="Date Received">{{paymentReceived.datePayment | formatDate}}</DataItem>
                  </template>
                </DataPageArea>

                <div class="row mt-4" v-if="hasInvoicePaymentAllocations">
                  <div class="col-12">
                    <h3>Invoice Allocations</h3>
                    <StaticDataTable class="static-table-list">
                      <template v-slot:header>
                        <tr>
                          <th>Invoice</th>
                          <th>Invoice Status </th>
                          <th>Payment Status</th>
                          <th>Payment Date</th>
                          <th>Invoice Amount</th>
                          <th>Payment Amount</th>
                        </tr>
                      </template>
                      <template v-slot:body>
                        <tr v-for="invoicePayment in paymentReceived.invoicePaymentAllocations" :key="invoicePayment.id">
                          <td class="font-weight-bold">
                            <LinkInvoice :id="invoicePayment.invoiceId" :name="invoicePayment.invoice.sequenceNumber" />
                          </td>
                          <td>
                            <LabelBadge :labelClass="`label-invoice-status-${invoicePayment.invoice.status}`">{{invoicePayment.invoice.status | formatLookupName(InvoiceStatusTypeLookup)}}</LabelBadge>
                          </td>
                          <td>
                            <LabelBadge :labelClass="`label-payment-status-${invoicePayment.status}`">{{invoicePayment.status | formatLookupName(PaymentStatusTypeLookup)}}</LabelBadge>
                          </td>
                          <td>{{invoicePayment.dateAllocated | formatDateTime}}</td>
                          <td>{{invoicePayment.invoice.totalPrice | formatCurrency}}</td>
                          <td class="font-weight-bold">{{invoicePayment.allocatedAmount | formatCurrency}}</td>
                        </tr>
                      </template>
                    </StaticDataTable>
                  </div>
                </div>

                <div class="row mt-4" v-if="hasOverPayments">
                  <div class="col-12">
                    <h3>Overpayments</h3>
                    <StaticDataTable class="static-table-list">
                      <template v-slot:header>
                        <tr>
                          <th>Over Payment</th>
                          <th>Over Payment Status</th>
                          <th>Linked Order</th>
                          <th>Payment Date</th>
                          <th>Payment Amount</th>
                        </tr>
                      </template>
                      <template v-slot:body>
                        <tr v-for="overPayment in paymentReceived.overPayments" :key="overPayment.id">
                          <td class="font-weight-bold">
                            <LinkOverPayment :id="overPayment.id" :name="overPayment.sequenceNumber" />
                          </td>
                          <td>
                            <LabelBadge :labelClass="`label-payment-status-${overPayment.status}`">{{overPayment.status | formatLookupName(OverpaymentStatusTypeLookup)}}</LabelBadge>
                          </td>
                          <td>
                            <LinkOrder v-if="overPayment.order" :id="overPayment.order.id" :name="overPayment.order.sequenceNumber" />
                            <span v-if="!overPayment.order">-</span>
                          </td>
                          <td>{{overPayment.dateCreated | formatDateTime}}</td>
                          <td class="font-weight-bold">{{overPayment.amount | formatCurrency}}</td>
                        </tr>
                      </template>
                    </StaticDataTable>
                  </div>
                </div>
              </div>
            </TabPage>
            
            <TabPage tabid="tab-payment-activitylog">
              <div class="row">
                <div class="col-lg-12">
                  <ActivityLogStream ref="activityStream" :url="activityStreamURL" :linkType="LinkTypes.paymentReceived" :linkId="paymentReceived.id">
                    <template v-slot:title><h3>Recent Activity</h3></template>
                  </ActivityLogStream>
                </div>
              </div>
            </TabPage>
          </template>
        </TabbedBox>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import Utils from '@/utils/utils'
import pdfMixin from '@/mixins/pdf'

import SendPaymentReceivedDialog from '@/modules/accounts/views/sendPaymentReceivedDialog'
import PaymentReceivedEditDialog from '@/modules/accounts/views/paymentReceivedEditDialog'
import PaymentReceivedCancelDialog from '@/modules/accounts/views/paymentReceivedCancelDialog'

import { InvoiceStatusTypeLookup, PaymentStatusTypes, PaymentStatusTypeLookup, PaymentTypes, PaymentTypeLookup, OverpaymentStatusTypeLookup, LinkTypes } from '@/store/types'


export default {
  name: 'PaymentReceived',
  mixins: [form, pdfMixin],
  components: {
  },
  computed: {
    canEditPayment() {
      if(!this.userHasUpdatePermission(this.PermissionTypes.AccountsPermissionTypes.payment))
        return false
      if(this.paymentReceived.paymentType == PaymentTypes.external)
        return false
      if(this.paymentReceived.status == PaymentStatusTypes.cancelled)
        return false
      return true
    },
    canCancelPayment() {
      if(!this.userHasUpdatePermission(this.PermissionTypes.AccountsPermissionTypes.payment))
        return false
      if(this.paymentReceived.status == PaymentStatusTypes.cancelled)
        return false
      return true
    },
    activityStreamURL() {
      if(!this.$data.paymentReceivedId)
        return null
      return `/accounts/paymentreceiveds/${this.$data.paymentReceivedId}/activitystream`
    },
    hasIntegrations() {
      return this.integrationList.filter(integration => integration.active).length > 0
    },
    integrationList() {
      if(this.$data.paymentReceived.integrationData == null)
        return []
      return Object.values(this.$data.paymentReceived.integrationData)        
    },
    hasInvoicePaymentAllocations() {
      if(this.$data.paymentReceived.invoicePaymentAllocations == null)
        return false
      return this.$data.paymentReceived.invoicePaymentAllocations.length > 0
    },
    hasOverPayments() {
      if(this.$data.paymentReceived.overPayments == null)
        return false
      return this.$data.paymentReceived.overPayments.length > 0
    },
    isPaymentCancelled() {
      return this.paymentReceived.status == PaymentStatusTypes.cancelled
    }
  },
  data() {
    return {
      paymentReceivedId: null,
      paymentReceived: {}
    }
  },
  created() {
    this.LinkTypes = LinkTypes
    this.InvoiceStatusTypeLookup = InvoiceStatusTypeLookup
    this.PaymentStatusTypeLookup = PaymentStatusTypeLookup
    this.PaymentTypeLookup = PaymentTypeLookup    
    this.OverpaymentStatusTypeLookup = OverpaymentStatusTypeLookup
  },
  mounted() {
    this.$data.paymentReceivedId = this.$route.params.id

    // Load the payment received
    this.requestData()
  },
  beforeRouteUpdate (to, from, next) {
    next()
    this.$data.paymentReceivedId = this.$route.params.id

    this.pageRefreshed()
    this.requestData()
  },
  methods: {
    reloadData() {
      this.$refs.activityStream.reloadData()
      this.requestData()
    },
    requestData() {
      this.pageLoadData(Promise.all([
        this.$apiService.accountsService.getPaymentReceived(this.$data.paymentReceivedId),
      ]))
      .then(results => {
        this.paymentReceived = results[0].data
      })
      .catch(err => this.handlePageLoadError(err))
    },
    sendPaymentReceipt() {
      this.displayModal(SendPaymentReceivedDialog, { 
        paymentReceivedId: parseInt(this.$data.paymentReceivedId),
        contactId: parseInt(this.paymentReceived.contactId)
      })
      .then((success) => {
        if(success) {
          this.successToast('Receipt Sent')
          this.reloadData()
        }
      })
    },
    displayPaymentReceivedReport(options = {}) {
      var params = $.param(options)
      this.displayReportPDF(`/accounts/paymentreceiveds/${this.$data.paymentReceivedId}/report?${params}`)
    },
    updatePaymentReceived() {
      this.displayModal(PaymentReceivedEditDialog, { 
        paymentReceivedId: parseInt(this.$data.paymentReceivedId)
      })
      .then((success) => {
        if(success) {
          this.successToast('Payment Details Updated')
          this.reloadData()
        }
      })
    },
    cancelPaymentReceived() {
      this.displayModal(PaymentReceivedCancelDialog, { 
        paymentReceivedId: parseInt(this.$data.paymentReceivedId)
      })
      .then((success) => {
        if(success) {
          this.successToast('Payment Cancelled')
          this.reloadData()
        }
      })
    }
  }
}
</script>
